import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { getConfigValue } from "../../util/getConfigValue";
import { AppContext } from "../../util/AppContext";
import { isMarketProductMappingEnabled, getMarketIdsForProduct } from "../../util/marketProductMapping";
import PropTypes from 'prop-types';

AddTerritoryModal.propTypes = {
    show: PropTypes.bool.isRequired,
    setShow: PropTypes.func.isRequired,
    applicationData: PropTypes.array.isRequired,
    addSelectedTerritories: PropTypes.func.isRequired,
    productId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    filterByMarketMapping: PropTypes.bool
};

function territoryMatches(t, target) {
    return String(t.id) === String(target.id) ||
        (Boolean(t.name) && Boolean(target.name) && t.name.trim().toLowerCase() === target.name.trim().toLowerCase());
}

function findMarketForTerritory(markets, territory) {
    if (!Array.isArray(markets)) {
        return null;
    }
    for (const market of markets) {
        if (Array.isArray(market.territories)) {
            for (const terr of market.territories) {
                if (territoryMatches(terr, territory)) {
                    return market;
                }
            }
        }
    }
    return null;
}

function findMarketById(markets, marketId) {
    if (!Array.isArray(markets) || marketId === null || marketId === undefined) {
        return null;
    }
    for (const market of markets) {
        if (Number.parseInt(market.id, 10) === marketId) {
            return market;
        }
    }
    return null;
}

function isTerritoryAvailableForProduct(territory, productId) {
    if (productId === null || productId === undefined || !Array.isArray(territory?.productId) || territory.productId.length === 0) {
        return true;
    }
    return territory.productId.some(p => String(p) === String(productId));
}

function enrichTerritories(territories, allMarkets, allowedMarketIds) {
    if (!Array.isArray(territories)) {
        return [];
    }

    const candidateMarkets = Array.isArray(allMarkets)
        ? allMarkets.filter(m => allowedMarketIds.includes(Number.parseInt(m.id, 10)))
        : [];

    return territories.map(t => {
        let marketId = t.marketId !== null && t.marketId !== undefined ? Number.parseInt(t.marketId, 10) : null;
        let marketName = t.marketName;

        if (marketId !== null && marketId !== undefined) {
            if (!marketName && allMarkets?.length > 0) {
                const found = findMarketById(allMarkets, marketId);
                if (found) {
                    marketName = found.name;
                }
            }
            return {
                ...t,
                marketId,
                marketName
            };
        }

        const matchedMarket = findMarketForTerritory(candidateMarkets, t) || findMarketForTerritory(allMarkets, t);
        if (matchedMarket) {
            return {
                ...t,
                marketId: Number.parseInt(matchedMarket.id, 10),
                marketName: matchedMarket.name
            };
        }

        return t;
    });
}

function AddTerritoryModal({ show, setShow, applicationData, addSelectedTerritories, productId, filterByMarketMapping }) {
    const [appState] = useContext(AppContext);
    const isMappingEnforced = filterByMarketMapping !== null && filterByMarketMapping !== undefined
        ? filterByMarketMapping
        : isMarketProductMappingEnabled(appState);
    const [territoryList, setTerritoryList] = useState([]);
    const [filterTerritory, setFilterTerritory] = useState([]);
    const [allMarkets] = useState(() => {
        try {
            return JSON.parse(sessionStorage.getItem('marketsForUser')) || [];
        } catch {
            return [];
        }
    });
    const [searchInput, setSearchInput] = useState("");
    const [noResultsFound, setNoResultsFound] = useState(false);
    const [savedList, setSavedList] = useState([]);
    const defaultSelectionText = getConfigValue(appState.config?.TerritoryModal?.DefaultSelectionText, appState)?.Value || null;
    const [selectedMarket, setSelectedMarket] = useState(null);

    // when market/product mapping is enforced, only markets mapped to this product are selectable
    const marketList = useMemo(() => {
        if (!isMappingEnforced) {
            return allMarkets;
        }
        const allowedMarketIds = getMarketIdsForProduct(appState?.marketProductMap, productId);
        return allMarkets.filter(m => allowedMarketIds.includes(Number.parseInt(m.id, 10)));
    }, [allMarkets, appState?.marketProductMap, productId, isMappingEnforced]);

    const pruneToAllowedMarkets = useCallback((territories) => {
        if (!Array.isArray(territories)) {
            return [];
        }

        const allowedMarketIds = isMappingEnforced && productId !== null && productId !== undefined
            ? getMarketIdsForProduct(appState?.marketProductMap, productId)
            : allMarkets.map(m => Number.parseInt(m.id, 10));

        const enriched = enrichTerritories(territories, allMarkets, allowedMarketIds);

        if (!isMappingEnforced || productId === null || productId === undefined) {
            return enriched;
        }

        return enriched.filter(t => {
            if (t.marketId === null || t.marketId === undefined) {
                return true;
            }
            return allowedMarketIds.includes(Number.parseInt(t.marketId, 10));
        });
    }, [allMarkets, appState?.marketProductMap, productId, isMappingEnforced]);

    const matchesTerritory = useCallback((t, target) => {
        if (!territoryMatches(t, target)) {
            return false;
        }
        if (!isMappingEnforced) {
            return true;
        }
        if (t.marketId !== null && t.marketId !== undefined && target.marketId !== null && target.marketId !== undefined) {
            return Number(t.marketId) === Number(target.marketId);
        }
        return true;
    }, [isMappingEnforced]);

    const isTerritorySelected = useCallback((territory) => {
        return savedList.some(a => matchesTerritory(a, territory));
    }, [savedList, matchesTerritory]);

    useEffect(() => {
        if (!Array.isArray(marketList) || marketList.length === 0) {
            setTerritoryList([]);
            return;
        }

        const territories = [];
        for (const market of marketList) {
            const terrs = Array.isArray(market.territories) ? market.territories : [];
            for (const territory of terrs) {
                if (isTerritoryAvailableForProduct(territory, productId)) {
                    territories.push({
                        id: territory.id,
                        name: territory.name,
                        marketId: Number.parseInt(market.id, 10),
                        prescribers: territory.prescribers,
                        marketName: market.name
                    });
                }
            }
        }
        setTerritoryList(territories);
    }, [marketList, productId]);

    // clear a market selection that is no longer available
    useEffect(() => {
        if (selectedMarket !== null && !marketList.some(m => Number.parseInt(m.id, 10) === selectedMarket)) {
            setSelectedMarket(null);
        }
    }, [marketList, selectedMarket]);

    useEffect(() => {
        setSavedList(pruneToAllowedMarkets(applicationData));
    }, [applicationData, pruneToAllowedMarkets]);

    useEffect(() => {
        setFilterTerritory(territoryList);
    }, [territoryList, savedList]);

    useEffect(() => {
        if (searchInput.length > 0) {
            const results = filterByValue(territoryList, searchInput);
            setFilterTerritory(results);
            setNoResultsFound(results.length === 0);
        } else {
            const results = filterByMarket(territoryList);
            setFilterTerritory(results);
            setNoResultsFound(results.length === 0);
        }

    }, [searchInput, territoryList, selectedMarket, savedList]);

    function handleClose() {
        setSavedList(pruneToAllowedMarkets(applicationData));
        setShow(false);
    };

    function handleMarketChange(event) {
        const value = event.target.value;

        if (!value || value === defaultSelectionText) {
            setSelectedMarket(null);
        } else {
            const foundMarket = marketList.find(f => f.name === value);
            setSelectedMarket(foundMarket ? Number.parseInt(foundMarket.id, 10) : null);
        }
    };

    function handleChange(event) {
        setSearchInput(event.target.value);
    };

    function filterByValue(array, searchString) {
        if (!Array.isArray(array)) {
            return [];
        }

        if (selectedMarket !== null) {
            return array.filter(element => element.name.toLowerCase().includes(searchString.toLowerCase()) && Number(element.marketId) === Number(selectedMarket));
        }

        return array.filter(element => element.name.toLowerCase().includes(searchString.toLowerCase()));
    };

    function filterByMarket(array) {
        if (!Array.isArray(array)) {
            return [];
        }

        if (selectedMarket !== null) {
            return array.filter(element => Number(element.marketId) === Number(selectedMarket));
        }

        return array;
    };

    function handleSelectedTerritory(territory, e) {
        const isChecked = e.target.checked;

        if (isChecked) {
            if (!savedList.some(t => matchesTerritory(t, territory))) {
                setSavedList([...savedList, territory]);
            }
        } else {
            setSavedList(savedList.filter(e => !matchesTerritory(e, territory)));
        }
    };

    function handleAdd() {
        addSelectedTerritories(savedList);
        setShow(false);
    }

    function isTerritoryDisabled(territory) {
        // territory ids are not unique across markets, so match on both id and marketId
        const affectedTerritory = savedList.find(t => matchesTerritory(t, territory));
        return affectedTerritory?.type === "DataLoader" || false;
    }

    function handleCheckUncheckAll() {
        // DataLoader-sourced territories are locked and must not be affected by check/uncheck all
        const toggleableFilterTerritory = filterTerritory.filter(t => !isTerritoryDisabled(t));
        const allToggleableSelected = toggleableFilterTerritory.length > 0 &&
            toggleableFilterTerritory.every(t => isTerritorySelected(t));

        if (!allToggleableSelected) {
            const unselectedToAdd = toggleableFilterTerritory.filter(t => !isTerritorySelected(t));
            setSavedList([...savedList, ...unselectedToAdd]);
        } else {
            setSavedList(savedList.filter(t => !toggleableFilterTerritory.some(ft => matchesTerritory(t, ft))));
        }
    }

    const marketNameStyle = {
        marginLeft: '0.9rem'
    };

    return (
        <Modal show={show} onHide={handleClose} scrollable={true} >
            <Modal.Header>
                <Modal.Title className="modal-title type-20 fs-5">{getConfigValue(appState?.config?.Territory?.AddAssignment, appState)?.Value || null}</Modal.Title>
            </Modal.Header>
            <div className="modal-subheader px-3 pt-2 pb-1 border-bottom">
                {marketList.length > 1 && (
                    <>
                        <label htmlFor="territories-market-select" className="form-label">{getConfigValue(appState.config?.TerritoryModal?.SelectMarket?.Label, appState)?.Value || null}</label>
                        <select
                            id="territories-market-select"
                            value={selectedMarket !== null && selectedMarket !== undefined ? (marketList.find(m => Number.parseInt(m.id, 10) === selectedMarket)?.name || '') : ''}
                            onChange={handleMarketChange}
                            className="form-select mb-3"
                            aria-label="Select a market"
                        >
                            <option value="">{defaultSelectionText || "All Markets"}</option>
                            {marketList.map((market) => (
                                <option key={market.id} value={market.name}>{market.name}</option>
                            ))}
                        </select>
                    </>
                )}
                <label
                    htmlFor="territories-search"
                    className="form-label">
                    {getConfigValue(appState?.config?.Territory?.Search, appState)?.Value || null}
                </label>
                <input
                    type="text"
                    className="form-control icon-search"
                    id="territories-search"
                    aria-label="Search territories"
                    onChange={handleChange}
                    value={searchInput}>
                </input>
                <button className="btn btn-outline-primary btn-sm btn-icon my-3" onClick={handleCheckUncheckAll} >{getConfigValue(appState?.config?.Territory?.CheckUncheckAll, appState)?.Value || null}<span className="fa-regular fa-check btn-icon"></span></button>
            </div>
            <Modal.Body className="modal-body" >

                <ul className="list-group list-group-flush">
                    {filterTerritory.length > 0 && filterTerritory.map((territory) => {
                        const compositeKey = `${territory.marketId}-${territory.id}`;
                        return (
                            <li className="list-group-item px-0 py-3 d-flex align-items-center" key={compositeKey}>
                                <div className="form-check">
                                    <input
                                        checked={isTerritorySelected(territory)}
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`add-territory-${compositeKey}`}
                                        onChange={(e) => handleSelectedTerritory(territory, e)}
                                        disabled={isTerritoryDisabled(territory)}
                                    ></input>
                                    <label className="form-check-label ms-2" htmlFor={`add-territory-${compositeKey}`}>{territory.name}</label>
                                    <span className="d-block type-14 color-grey-d1" style={marketNameStyle}>{territory.marketName}</span>
                                </div>
                            </li>
                        )
                    })}

                </ul>

            </Modal.Body>
            <Modal.Footer className="modal-footer">
                <div className="container-fluid mx-0">
                    <div className="row justify-content-md-end px-0">
                        <div className="col-12 col-md-10 px-0">
                            <div className="d-flex w-100 flex-column flex-md-row-reverse">
                                <Button
                                    className="btn btn-primary btn-lg mb-3 mb-md-0 ms-md-3 flex-fill w-100"
                                    onClick={handleAdd}>
                                    Add
                                </Button>
                                <Button
                                    className="btn btn-secondary btn-lg flex-fill w-100"
                                    data-bs-dismiss="modal"
                                    onClick={handleClose}>
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );
}
export default AddTerritoryModal;